import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Search,
  Download,
  Filter,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Zap,
  X,
  Plus,
  Trash2,
  Info,
  CheckCircle2,
  Clock,
  FileDown,
  Ban,
  Calendar,
  Loader2,
  ArrowDownLeft,
  ArrowUpRight,
  MoreHorizontal,
  Settings2,
  Edit3
} from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useWorkspace } from '../hooks/useWorkspace'
import { useToast } from '../hooks/useToast'
import { IconPicker } from '../components/CategoryIcon'

// ─── Types ──────────────────────────────────────────────────────────────────

interface Transaction {
  id: string
  date: string
  description: string
  amount: number
  type: 'credit' | 'debit'
  category_id: string
  category_name: string
  cost_type: 'fixed' | 'variable'
  upload_id: string
  counterparty?: string
  journal_status?: 'to_prepare' | 'ready' | 'exported' | 'excluded'
}

interface Category {
  id: string
  name: string
  icon: string
  color: string
  cost_type: string
}

interface AutomationRule {
  id: string
  name: string
  conditions: AutomationCondition[]
  match_mode: 'any' | 'all'
  action_type: 'assign_counterpart' | 'assign_category' | 'set_cost_type'
  action_value: string
  enabled: boolean
}

interface AutomationCondition {
  field: 'amount_type' | 'description' | 'amount'
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than'
  value: string
}

type JournalTab = 'to_prepare' | 'ready' | 'exported' | 'excluded'

// ─── Predefined colors ─────────────────────────────────────────────────────

const CATEGORY_COLORS = ['#ef4444', '#004aad', '#5de0e6', '#84cc16', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899']

// ─── Component ──────────────────────────────────────────────────────────────

export default function Categorize() {
  const queryClient = useQueryClient()
  const { language } = useLanguage()
  const { currentWorkspace } = useWorkspace()
  const { showToast } = useToast()
  
  const [activeTab, setActiveTab] = useState<JournalTab>('ready')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showAutomationModal, setShowAutomationModal] = useState(false)
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false)
  const [dateRange, setDateRange] = useState('365')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [filterType, setFilterType] = useState<'all' | 'debit' | 'credit'>('all')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [sortField, setSortField] = useState<'date' | 'amount' | 'description'>('date')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const filterRef = useRef<HTMLDivElement>(null)

  // New category state
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryIcon, setNewCategoryIcon] = useState('folder')
  const [newCategoryColor, setNewCategoryColor] = useState(CATEGORY_COLORS[0])

  // Automation editing state
  const [editingAutomation, setEditingAutomation] = useState<Partial<AutomationRule>>({
    conditions: [{ field: 'amount_type', operator: 'equals', value: 'debit' }],
    match_mode: 'any',
    action_type: 'assign_counterpart',
    action_value: '',
    enabled: true,
  })

  // Close dropdown menu on outside click
  useEffect(() => {
    if (!openMenuId) return
    const handler = () => setOpenMenuId(null)
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [openMenuId])

  // Close filter panel on outside click
  useEffect(() => {
    if (!showFilterPanel) return
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilterPanel(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showFilterPanel])

  // ─── API Queries ────────────────────────────────────────────────────────

  const { data: categoriesData } = useQuery<Category[]>({
    queryKey: ['categories', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/categories/')
      return response.data.categories || response.data || []
    },
    enabled: !!currentWorkspace?.id,
  })

  const { data: transactionsData, isLoading } = useQuery<{ data?: Transaction[]; transactions?: Transaction[] }>({
    queryKey: ['transactions-journal', currentWorkspace?.id, dateRange],
    queryFn: async () => {
      const response = await api.get('/transactions/?per_page=500')
      return response.data
    },
    enabled: !!currentWorkspace?.id,
  })

  const categories = categoriesData || []

  const allTransactions = useMemo(() => {
    if (!transactionsData) return []
    const raw = transactionsData.data || transactionsData.transactions || []
    // Filter by date range
    if (dateRange === 'all') return raw
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - parseInt(dateRange))
    return raw.filter(t => new Date(t.date) >= cutoff)
  }, [transactionsData, dateRange])

  // ─── Mutations ──────────────────────────────────────────────────────────

  const updateTransactionMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Transaction> }) => {
      const response = await api.patch(`/transactions/${id}`, updates)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions-journal'] })
    },
  })

  const createCategoryMutation = useMutation({
    mutationFn: async (data: { name: string; icon: string; color: string }) => {
      const response = await api.post('/categories/', data)
      return response.data
    },
    onSuccess: () => {
      showToast('success', language === 'it' ? 'Categoria creata' : 'Category created')
      setShowNewCategoryModal(false)
      setNewCategoryName('')
      setNewCategoryIcon('folder')
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
    onError: () => {
      showToast('error', language === 'it' ? 'Errore creazione categoria' : 'Category creation error')
    },
  })

  // ─── Computed ───────────────────────────────────────────────────────────

  // Split transactions by journal status (simulated from existing data)
  const { toPrepare, ready, exported, excluded } = useMemo(() => {
    const tp: Transaction[] = []
    const r: Transaction[] = []
    const e: Transaction[] = []
    const ex: Transaction[] = []
    
    for (const t of allTransactions) {
      const status = t.journal_status || (t.category_id && t.category_id !== 'other' ? 'ready' : 'to_prepare')
      switch (status) {
        case 'ready': r.push(t); break
        case 'exported': e.push(t); break
        case 'excluded': ex.push(t); break
        default: tp.push(t); break
      }
    }
    return { toPrepare: tp, ready: r, exported: e, excluded: ex }
  }, [allTransactions])

  const currentList = useMemo(() => {
    let list = activeTab === 'to_prepare' ? toPrepare
      : activeTab === 'ready' ? ready
      : activeTab === 'exported' ? exported
      : excluded
    
    // Text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      list = list.filter(t =>
        t.description.toLowerCase().includes(q) ||
        (t.counterparty?.toLowerCase().includes(q)) ||
        (t.category_name?.toLowerCase().includes(q))
      )
    }

    // Filter by type
    if (filterType === 'debit') list = list.filter(t => t.amount < 0)
    else if (filterType === 'credit') list = list.filter(t => t.amount > 0)

    // Filter by category
    if (filterCategory !== 'all') {
      if (filterCategory === 'uncategorized') {
        list = list.filter(t => !t.category_id || t.category_id === 'other')
      } else {
        list = list.filter(t => t.category_id === filterCategory)
      }
    }

    // Sort
    const sorted = [...list]
    sorted.sort((a, b) => {
      let cmp = 0
      if (sortField === 'date') cmp = a.date.localeCompare(b.date)
      else if (sortField === 'amount') cmp = Math.abs(a.amount) - Math.abs(b.amount)
      else if (sortField === 'description') cmp = a.description.localeCompare(b.description)
      return sortDirection === 'desc' ? -cmp : cmp
    })
    return sorted
  }, [activeTab, toPrepare, ready, exported, excluded, searchQuery, filterType, filterCategory, sortField, sortDirection])

  // ─── Helpers ────────────────────────────────────────────────────────────

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Math.abs(amount))

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === currentList.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(currentList.map(t => t.id)))
    }
  }

  const handleMarkReady = () => {
    selectedIds.forEach(id => {
      updateTransactionMutation.mutate({ id, updates: { journal_status: 'ready' } as Partial<Transaction> })
    })
    setSelectedIds(new Set())
    showToast('success', language === 'it' ? 'Scritture pronte per export' : 'Entries ready for export')
  }

  const handleExport = () => {
    // Build CSV from ready transactions
    const exportList = activeTab === 'ready' ? ready : currentList
    const lines = ['Data,Descrizione,Controparte,Dare,Avere,Categoria']
    exportList.forEach(t => {
      const debit = t.amount < 0 ? Math.abs(t.amount).toFixed(2) : ''
      const credit = t.amount > 0 ? t.amount.toFixed(2) : ''
      lines.push(`${t.date},"${t.description}","${t.counterparty || ''}",${debit},${credit},"${t.category_name || ''}"`)
    })
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `prima_nota_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    showToast('success', language === 'it' ? 'Export completato' : 'Export complete')
  }

  const tabDef = [
    { key: 'to_prepare' as JournalTab, label: language === 'it' ? 'Da preparare' : 'To prepare', count: toPrepare.length, icon: Clock },
    { key: 'ready' as JournalTab, label: language === 'it' ? 'Pronti per export' : 'Ready to export', count: ready.length, icon: CheckCircle2 },
    { key: 'exported' as JournalTab, label: language === 'it' ? 'Esportati' : 'Exported', count: exported.length, icon: FileDown },
    { key: 'excluded' as JournalTab, label: language === 'it' ? 'Esclusi' : 'Excluded', count: excluded.length, icon: Ban },
  ]

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-0 pt-1">
      {/* Page Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            {language === 'it' ? 'Prima Nota' : 'Bank Journal'}
          </h1>
          <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <Info className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAutomationModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Zap className="w-4 h-4 text-amber-500" />
            {language === 'it' ? 'Automazioni' : 'Automations'}
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            {language === 'it' ? 'Esporta scritture' : 'Export entries'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-slate-700">
        {tabDef.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setSelectedIds(new Set()) }}
            className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              activeTab === tab.key
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400'
            }`}>
              {tab.count}
            </span>
            {activeTab === tab.key && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={language === 'it' ? 'Cerca transazione...' : 'Search transaction...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Date range */}
          <div className="relative">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="appearance-none pl-8 pr-8 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 cursor-pointer focus:ring-1 focus:ring-blue-500"
            >
              <option value="30">{language === 'it' ? 'Ultimi 30 giorni' : 'Last 30 days'}</option>
              <option value="60">{language === 'it' ? 'Ultimi 60 giorni' : 'Last 60 days'}</option>
              <option value="90">{language === 'it' ? 'Ultimi 90 giorni' : 'Last 90 days'}</option>
              <option value="180">{language === 'it' ? 'Ultimi 6 mesi' : 'Last 6 months'}</option>
              <option value="365">{language === 'it' ? 'Ultimo anno' : 'Last year'}</option>
              <option value="all">{language === 'it' ? 'Tutti' : 'All'}</option>
            </select>
            <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>

          {/* Filter button */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`p-2 border rounded-lg transition-colors ${
                filterType !== 'all' || filterCategory !== 'all'
                  ? 'text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              <Filter className="w-4 h-4" />
            </button>
            {showFilterPanel && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg z-50 p-3 space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                    {language === 'it' ? 'Tipo' : 'Type'}
                  </label>
                  <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value as 'all' | 'debit' | 'credit')}
                    className="w-full text-xs bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded px-2 py-1.5 text-gray-700 dark:text-gray-200"
                  >
                    <option value="all">{language === 'it' ? 'Tutti' : 'All'}</option>
                    <option value="debit">{language === 'it' ? 'Uscite' : 'Debits'}</option>
                    <option value="credit">{language === 'it' ? 'Entrate' : 'Credits'}</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                    {language === 'it' ? 'Categoria' : 'Category'}
                  </label>
                  <select
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                    className="w-full text-xs bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded px-2 py-1.5 text-gray-700 dark:text-gray-200"
                  >
                    <option value="all">{language === 'it' ? 'Tutte' : 'All'}</option>
                    <option value="uncategorized">{language === 'it' ? 'Non categorizzate' : 'Uncategorized'}</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {(filterType !== 'all' || filterCategory !== 'all') && (
                  <button
                    onClick={() => { setFilterType('all'); setFilterCategory('all') }}
                    className="w-full text-xs text-center text-red-500 hover:text-red-600 py-1"
                  >
                    {language === 'it' ? 'Resetta filtri' : 'Reset filters'}
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Sort button */}
          <button
            onClick={() => {
              if (sortField === 'date' && sortDirection === 'desc') setSortDirection('asc')
              else if (sortField === 'date' && sortDirection === 'asc') { setSortField('amount'); setSortDirection('desc') }
              else if (sortField === 'amount' && sortDirection === 'desc') setSortDirection('asc')
              else if (sortField === 'amount' && sortDirection === 'asc') { setSortField('description'); setSortDirection('asc') }
              else { setSortField('date'); setSortDirection('desc') }
            }}
            title={`${sortField} ${sortDirection}`}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            <ArrowUpDown className="w-4 h-4" />
          </button>
        </div>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {selectedIds.size} {language === 'it' ? 'selezionate' : 'selected'}
            </span>
            {activeTab === 'to_prepare' && (
              <button
                onClick={handleMarkReady}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {language === 'it' ? 'Segna come pronte' : 'Mark as ready'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : currentList.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <FileDown className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
            {language === 'it' ? 'Nessuna scrittura' : 'No entries'}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {language === 'it' ? 'Non ci sono transazioni in questa sezione' : 'No transactions in this section'}
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800">
          {/* Table header */}
          <div className="grid grid-cols-[40px_1fr_180px_120px_120px_48px] gap-0 px-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border-b border-gray-200 dark:border-slate-700 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedIds.size === currentList.length && currentList.length > 0}
                onChange={toggleSelectAll}
                className="w-3.5 h-3.5 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
            </div>
            <div>{language === 'it' ? 'TRANSAZIONE BANCARIA' : 'BANK TRANSACTION TITLE'}</div>
            <div>{language === 'it' ? 'Controparti' : 'Counterparts'}</div>
            <div className="text-right">{language === 'it' ? 'DARE' : 'DEBIT'}</div>
            <div className="text-right">{language === 'it' ? 'AVERE' : 'CREDIT'}</div>
            <div />
          </div>

          {/* Table rows */}
          <div className="divide-y divide-gray-100 dark:divide-slate-700/50">
            {currentList.map(transaction => {
              const isExpanded = expandedRows.has(transaction.id)
              const isSelected = selectedIds.has(transaction.id)
              const isDebit = transaction.amount < 0
              const category = categories.find(c => c.id === transaction.category_id)

              return (
                <div key={transaction.id}>
                  <div 
                    className={`grid grid-cols-[40px_1fr_180px_120px_120px_48px] gap-0 px-4 py-3 items-center cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'hover:bg-gray-50 dark:hover:bg-slate-700/30'
                    }`}
                    onClick={() => toggleRow(transaction.id)}
                  >
                    {/* Checkbox */}
                    <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(transaction.id)}
                        className="w-3.5 h-3.5 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </div>

                    {/* Transaction title + date */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-gray-400 flex-shrink-0">
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </div>
                      <div className={`p-1 rounded ${isDebit ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
                        {isDebit 
                          ? <ArrowDownLeft className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
                          : <ArrowUpRight className="w-3.5 h-3.5 text-green-500 dark:text-green-400" />
                        }
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {transaction.description}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {formatDate(transaction.date)}
                        </p>
                      </div>
                    </div>

                    {/* Counterpart / Category */}
                    <div className="min-w-0">
                      {category ? (
                        <div className="flex items-center gap-1.5">
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: category.color }}
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                            {category.name}
                          </span>
                        </div>
                      ) : transaction.counterparty ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">
                          {transaction.counterparty}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300 dark:text-gray-600 italic">—</span>
                      )}
                    </div>

                    {/* Debit */}
                    <div className="text-right">
                      {isDebit && (
                        <span className="text-sm font-medium tabular-nums text-red-600 dark:text-red-400">
                          {formatCurrency(transaction.amount)}
                        </span>
                      )}
                    </div>

                    {/* Credit */}
                    <div className="text-right">
                      {!isDebit && (
                        <span className="text-sm font-medium tabular-nums text-green-600 dark:text-green-400">
                          {formatCurrency(transaction.amount)}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="relative flex justify-end" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === transaction.id ? null : transaction.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                      {openMenuId === transaction.id && (
                        <div className="absolute right-0 top-8 z-50 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 py-1">
                          {activeTab !== 'to_prepare' && (
                            <button
                              onClick={() => {
                                updateTransactionMutation.mutate({ id: transaction.id, updates: { journal_status: 'to_prepare' } as Partial<Transaction> })
                                setOpenMenuId(null)
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                              {language === 'it' ? 'Da preparare' : 'To prepare'}
                            </button>
                          )}
                          {activeTab !== 'ready' && (
                            <button
                              onClick={() => {
                                updateTransactionMutation.mutate({ id: transaction.id, updates: { journal_status: 'ready' } as Partial<Transaction> })
                                setOpenMenuId(null)
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-green-700 dark:text-green-400 hover:bg-gray-50 dark:hover:bg-slate-700"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {language === 'it' ? 'Pronto per export' : 'Ready to export'}
                            </button>
                          )}
                          {activeTab !== 'excluded' && (
                            <button
                              onClick={() => {
                                updateTransactionMutation.mutate({ id: transaction.id, updates: { journal_status: 'excluded' } as Partial<Transaction> })
                                setOpenMenuId(null)
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700"
                            >
                              <Ban className="w-3.5 h-3.5" />
                              {language === 'it' ? 'Escludi' : 'Exclude'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded row detail */}
                  {isExpanded && (
                    <div className="px-4 py-3 bg-gray-50/70 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-700/50">
                      <div className="ml-10 grid grid-cols-2 gap-6">
                        {/* Left: Details */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                              {language === 'it' ? 'Dettagli transazione' : 'Transaction details'}
                            </p>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 dark:text-gray-400">{language === 'it' ? 'Data' : 'Date'}</span>
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{formatDate(transaction.date)}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 dark:text-gray-400">{language === 'it' ? 'Importo' : 'Amount'}</span>
                                <span className={`text-xs font-medium ${isDebit ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                  {formatCurrency(transaction.amount)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 dark:text-gray-400">{language === 'it' ? 'Tipo costo' : 'Cost type'}</span>
                                <select
                                  value={transaction.cost_type || 'variable'}
                                  onChange={(e) => {
                                    updateTransactionMutation.mutate({
                                      id: transaction.id,
                                      updates: { cost_type: e.target.value as 'fixed' | 'variable' }
                                    })
                                    showToast('success', language === 'it' ? 'Tipo costo aggiornato' : 'Cost type updated')
                                  }}
                                  className="text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded px-2 py-0.5 cursor-pointer focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="fixed">{language === 'it' ? 'Fisso' : 'Fixed'}</option>
                                  <option value="variable">{language === 'it' ? 'Variabile' : 'Variable'}</option>
                                </select>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 dark:text-gray-400">{language === 'it' ? 'Controparte' : 'Counterparty'}</span>
                                <input
                                  type="text"
                                  defaultValue={transaction.counterparty || ''}
                                  placeholder="—"
                                  onBlur={(e) => {
                                    const val = e.target.value.trim()
                                    if (val !== (transaction.counterparty || '')) {
                                      updateTransactionMutation.mutate({
                                        id: transaction.id,
                                        updates: { counterparty: val } as Partial<Transaction>
                                      })
                                      showToast('success', language === 'it' ? 'Controparte aggiornata' : 'Counterparty updated')
                                    }
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                                  className="text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded px-2 py-0.5 w-40 text-right placeholder-gray-300 dark:placeholder-gray-600 focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right: Category assignment / actions */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                              {language === 'it' ? 'Categoria contabile' : 'Accounting category'}
                            </p>
                            <select
                              value={transaction.category_id || ''}
                              onChange={(e) => {
                                updateTransactionMutation.mutate({
                                  id: transaction.id,
                                  updates: { category_id: e.target.value }
                                })
                                showToast('success', language === 'it' ? 'Categoria aggiornata' : 'Category updated')
                              }}
                              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 cursor-pointer focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">{language === 'it' ? '— Nessuna categoria —' : '— No category —'}</option>
                              {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => setShowNewCategoryModal(true)}
                              className="flex items-center gap-1 mt-1.5 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              <Plus className="w-3 h-3" />
                              {language === 'it' ? 'Nuova categoria' : 'New category'}
                            </button>
                          </div>

                          {/* Quick actions */}
                          <div className="flex items-center gap-2">
                            {activeTab !== 'ready' && (
                              <button
                                onClick={() => {
                                  updateTransactionMutation.mutate({
                                    id: transaction.id,
                                    updates: { journal_status: 'ready' } as Partial<Transaction>
                                  })
                                  showToast('success', language === 'it' ? 'Pronta per export' : 'Ready for export')
                                }}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                {language === 'it' ? 'Pronto' : 'Ready'}
                              </button>
                            )}
                            {activeTab !== 'to_prepare' && (
                              <button
                                onClick={() => {
                                  updateTransactionMutation.mutate({
                                    id: transaction.id,
                                    updates: { journal_status: 'to_prepare' } as Partial<Transaction>
                                  })
                                  showToast('success', language === 'it' ? 'Spostata in Da preparare' : 'Moved to To prepare')
                                }}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                              >
                                <Edit3 className="w-3 h-3" />
                                {language === 'it' ? 'Da preparare' : 'To prepare'}
                              </button>
                            )}
                            {activeTab !== 'excluded' && (
                              <button
                                onClick={() => {
                                  updateTransactionMutation.mutate({
                                    id: transaction.id,
                                    updates: { journal_status: 'excluded' } as Partial<Transaction>
                                  })
                                  showToast('success', language === 'it' ? 'Esclusa' : 'Excluded')
                                }}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                              >
                                <Ban className="w-3 h-3" />
                                {language === 'it' ? 'Escludi' : 'Exclude'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Table footer */}
          <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800/80 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {currentList.length} {language === 'it' ? 'scritture' : 'entries'}
              {activeTab === 'to_prepare' && currentList.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedIds(new Set(currentList.map(t => t.id)))
                    handleMarkReady()
                  }}
                  className="ml-3 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {language === 'it' ? 'Segna tutte come pronte per export' : 'Mark all as ready to export'}
                </button>
              )}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">{language === 'it' ? 'Totale Dare' : 'Total Debit'}</span>
                <span className="text-sm font-semibold text-red-600 dark:text-red-400 tabular-nums">
                  {formatCurrency(currentList.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0))}
                </span>
              </div>
              <div className="w-px h-4 bg-gray-200 dark:bg-slate-700" />
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">{language === 'it' ? 'Totale Avere' : 'Total Credit'}</span>
                <span className="text-sm font-semibold text-green-600 dark:text-green-400 tabular-nums">
                  {formatCurrency(currentList.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Automation Modal */}
      {showAutomationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                    <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      {language === 'it' ? 'Modifica automazione' : 'Edit automation'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {language === 'it' ? 'Configura regole per categorizzare automaticamente' : 'Configure rules for automatic categorization'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAutomationModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-5 space-y-6">
              {/* When section */}
              <div>
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">
                  {language === 'it' ? 'Quando' : 'When'}
                </p>
                
                {/* Trigger config row */}
                <div className="flex flex-wrap items-center gap-2 mb-4 text-sm text-gray-700 dark:text-gray-300">
                  <span>{language === 'it' ? 'Le transazioni di tipo' : 'Transactions of type'}</span>
                  <select
                    value={editingAutomation.conditions?.[0]?.value === 'debit' ? 'debit' : 'credit'}
                    onChange={(e) => setEditingAutomation(prev => ({
                      ...prev,
                      conditions: [{ field: 'amount_type', operator: 'equals', value: e.target.value }]
                    }))}
                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="debit">{language === 'it' ? 'Addebito' : 'Debit'}</option>
                    <option value="credit">{language === 'it' ? 'Accredito' : 'Credit'}</option>
                  </select>
                  <span>{language === 'it' ? 'che corrispondono a' : 'that match'}</span>
                  <select
                    value={editingAutomation.match_mode}
                    onChange={(e) => setEditingAutomation(prev => ({ ...prev, match_mode: e.target.value as 'any' | 'all' }))}
                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="any">{language === 'it' ? 'qualsiasi' : 'any'}</option>
                    <option value="all">{language === 'it' ? 'tutte' : 'all'}</option>
                  </select>
                  <span>{language === 'it' ? 'le condizioni:' : 'conditions:'}</span>
                </div>
                
                {/* Condition: name contains */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl border border-gray-200 dark:border-slate-600">
                  <span className="text-sm text-gray-600 dark:text-gray-300 flex-shrink-0 font-medium">
                    {language === 'it' ? 'Il nome contiene' : 'Name contains'}
                  </span>
                  <input
                    type="text"
                    placeholder={language === 'it' ? 'es. Telecom, Enel...' : 'e.g. Telecom, Enel...'}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <button className="flex items-center gap-2 mt-3 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                  <Plus className="w-4 h-4" />
                  {language === 'it' ? 'Aggiungi condizione' : 'Add condition'}
                </button>
              </div>

              {/* Then section */}
              <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">
                  {language === 'it' ? 'Allora' : 'Then'}
                </p>
                <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-200 dark:border-green-800/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">
                      {language === 'it' ? 'Attribuisci controparti' : 'Attribute counterparts'}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder={language === 'it' ? 'Nome controparte...' : 'Counterpart name...'}
                    value={editingAutomation.action_value || ''}
                    onChange={(e) => setEditingAutomation(prev => ({ ...prev, action_value: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-green-200 dark:border-green-700 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowAutomationModal(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </button>
              <button
                onClick={() => {
                  setShowAutomationModal(false)
                  showToast('success', language === 'it' ? 'Automazione salvata' : 'Automation saved')
                }}
                className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-sm"
              >
                {language === 'it' ? 'Salva automazione' : 'Save automation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Category Modal */}
      {showNewCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {language === 'it' ? 'Nuova categoria' : 'New category'}
                </h3>
                <button
                  onClick={() => setShowNewCategoryModal(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {language === 'it' ? 'Nome' : 'Name'}
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder={language === 'it' ? 'es. Utenze, Marketing...' : 'e.g. Utilities, Marketing...'}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {language === 'it' ? 'Icona' : 'Icon'}
                </label>
                <IconPicker selectedIcon={newCategoryIcon} onSelect={setNewCategoryIcon} color={newCategoryColor} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  {language === 'it' ? 'Colore' : 'Color'}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {CATEGORY_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => setNewCategoryColor(color)}
                      className={`w-7 h-7 rounded-full transition-transform ${
                        newCategoryColor === color ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowNewCategoryModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600"
              >
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </button>
              <button
                onClick={() => createCategoryMutation.mutate({
                  name: newCategoryName,
                  icon: newCategoryIcon,
                  color: newCategoryColor,
                })}
                disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {createCategoryMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  language === 'it' ? 'Crea' : 'Create'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
